import { forwardRef } from 'react';
import { cn } from '@/utils/styles';
import { Slot } from '@radix-ui/react-slot';
import { ReturnButton } from '../shared/ReturnButton';
import { useIframeModeStore } from '@/hooks/useIframeModeStore';

export interface NavItemProps extends React.ComponentProps<'button'> {
  asChild?: boolean;
}

export const NavItem = forwardRef<HTMLButtonElement, NavItemProps>(
  ({ asChild, className, ...rest }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        className={cn(
          'flex min-w-16 flex-col gap-2 items-center justify-center typography-footnote text-tertiary hover:text-primary active:text-primary transition-colors [&_svg]:size-6',
          className,
        )}
        {...rest}
      />
    );
  },
);

export interface NavBarProps
  extends Omit<React.ComponentProps<'nav'>, 'title'> {
  title?: React.ReactNode;
  returnButtonTitle?: string;
  hideButtonLabel?: boolean;
  hideReturnButton?: boolean;
  buttonLabel?: React.ReactNode;
  onReturnClick?: () => void;
}

export const NavBar = ({
  title,
  children,
  className,
  buttonLabel,
  hideButtonLabel,
  hideReturnButton,
  returnButtonTitle = 'Back',
  onReturnClick,
  ...rest
}: NavBarProps) => {
  const { isIframeMode } = useIframeModeStore();

  if (isIframeMode) return null;

  return (
    <nav
      className={cn(
        'w-full flex flex-col md:flex-row items-center min-h-12 gap-2',
        className,
      )}
      {...rest}
    >
      <div className="flex flex-row items-center gap-2 w-full md:w-auto cursor-pointer">
        {!hideReturnButton && (
          <ReturnButton
            hideLabel={hideButtonLabel}
            children={buttonLabel || returnButtonTitle}
            onClick={onReturnClick}
          />
        )}

        {title && <span>{title}</span>}
      </div>

      <div className="w-full justify-end items-center flex flex-row">
        {children}
      </div>
    </nav>
  );
};
